import React, { useState, useEffect, useMemo } from "react"
import { FileText, Loader2, CheckCircle2, AlertTriangle } from "lucide-react"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Checkbox } from "src/components/ui/checkbox"
import { Label } from "src/components/ui/label"
import { Separator } from "src/components/ui/separator"
import { Skeleton } from "src/components/ui/skeleton"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"

import { CostComponentRow } from "src/features/documents/components/CostComponentRow"
import { useGenerateQuotation } from "src/features/documents/hooks/useDocumentSettings"
import { fetchQuotationContext } from "src/features/documents/api/documentSettingsApi"
import { formatMoney } from "src/features/deals/constants"

// Editable price components, in the order they appear on the PDF. Keys must match
// Unit::PRICE_COMPONENTS on the backend (minus `discount`, which is derived).
const COMPONENTS = [
  { key: "base", label: "Base Price" },
  { key: "plc", label: "Preferred Location (PLC)" },
  { key: "floor_rise", label: "Floor Rise" },
  { key: "parking", label: "Car Parking" },
  { key: "other", label: "Other Charges" },
]

// Reconciliation modes for when the negotiated price differs from the component
// sub-total.
const MODE_MATCH = "match" // show the gap as a discount line; total = negotiated
const MODE_ASIS = "asis"   // total = sub-total; negotiated is informational only

// Parse a raw input string to a finite non-negative number, else 0.
function num(raw) {
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 0
}

// Build the { base, plc, ... } string map from unit.price_components for prefill.
// When a unit has no itemised components but does carry a base_price, seed Base
// Price from it so the sheet isn't all zeros.
function prefillFromUnit(unit) {
  const pc = (unit && unit.price_components) || {}
  const next = {}
  for (const { key } of COMPONENTS) {
    const v = pc[key]
    next[key] = v == null || v === "" ? "" : String(v)
  }
  if (!next.base && unit?.base_price != null && Number(unit.base_price) > 0) {
    next.base = String(unit.base_price)
  }
  return next
}

const emptyComponents = () =>
  COMPONENTS.reduce((acc, { key }) => ({ ...acc, [key]: "" }), {})

// Pre-booking quotation / cost-sheet composer. Launched from a Deal; lets the
// seller adjust the unit's price components, apply the negotiated price as a
// discount, pick the CLP, and generate a branded PDF via the existing endpoint.
export function QuoteComposerDialog({ open, deal, onClose, onGenerated }) {
  const { toast } = useToast()
  const { mutate, isPending } = useGenerateQuotation()

  const [loading, setLoading] = useState(false)
  const [units, setUnits] = useState([])
  const [unitId, setUnitId] = useState(null)
  const [components, setComponents] = useState(emptyComponents)
  const [negotiated, setNegotiated] = useState("")
  const [mode, setMode] = useState(MODE_MATCH)
  const [includeClp, setIncludeClp] = useState(true)

  // Load context (units + deal value) whenever the dialog opens.
  useEffect(() => {
    if (!open || !deal?.id) return
    let active = true
    setLoading(true)
    setUnitId(null)
    setComponents(emptyComponents())
    fetchQuotationContext(deal.id)
      .then((ctx) => {
        if (!active) return
        setUnits(ctx.units || [])
        setUnitId(ctx.deal?.unit_id || ctx.units?.[0]?.id || null)
        setNegotiated(ctx.deal?.value != null ? String(ctx.deal.value) : "")
      })
      .catch(() => active && toast({ variant: "destructive", title: "Could not load unit pricing" }))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [open, deal?.id, toast])

  const selectedUnit = useMemo(() => units.find((u) => u.id === unitId) || null, [units, unitId])

  // Re-seed component inputs from the chosen unit's saved pricing.
  useEffect(() => {
    if (!selectedUnit) { setComponents(emptyComponents()); return }
    setComponents(prefillFromUnit(selectedUnit))
  }, [selectedUnit])

  const subtotal = useMemo(
    () => COMPONENTS.reduce((sum, { key }) => sum + num(components[key]), 0),
    [components],
  )

  const negotiatedNum = num(negotiated)
  const isMatch = mode === MODE_MATCH

  // In "match" mode a lower negotiated price becomes a discount; a higher one
  // isn't a discount (we never silently inflate charges), so it's clamped to 0
  // and surfaced as a warning.
  const discount = isMatch && negotiatedNum > 0 ? Math.max(0, subtotal - negotiatedNum) : 0
  const negotiatedExceedsSubtotal = isMatch && negotiatedNum > subtotal
  const total = isMatch && negotiatedNum > 0 ? Math.min(negotiatedNum, subtotal) : subtotal

  const milestoneCount = selectedUnit?.payment_plan?.milestones?.length || 0
  const hasClp = milestoneCount > 0

  function handleGenerate() {
    if (!unitId) {
      toast({ variant: "destructive", title: "Select a unit to quote" })
      return
    }
    if (subtotal <= 0) {
      toast({ variant: "destructive", title: "Add at least one price component" })
      return
    }

    // Only send components the user actually set, as numbers. Discount + explicit
    // agreement_value keep the PDF arithmetic transparent and authoritative.
    const cost_sheet = {}
    for (const { key } of COMPONENTS) {
      const n = num(components[key])
      if (n > 0) cost_sheet[key] = n
    }
    if (discount > 0) cost_sheet.discount = discount
    cost_sheet.agreement_value = total
    if (!includeClp || !hasClp) cost_sheet.skip_schedule = true

    mutate({ deal_id: deal.id, unit_id: unitId, cost_sheet })
      .then((result) => {
        toast({ title: "Quotation generated", description: "The cost sheet PDF is ready." })
        onGenerated?.(result)
        if (result?.view_url) window.open(result.view_url, "_blank")
        onClose()
      })
      .catch((errors) => {
        toast({
          variant: "destructive",
          title: "Could not generate quotation",
          description: Array.isArray(errors) ? errors[0] : "Please try again.",
        })
      })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Generate quotation</DialogTitle>
          <DialogDescription>
            Build a branded cost sheet for {deal?.lead?.name || "the customer"} from the unit’s pricing.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid gap-4 py-2 sm:grid-cols-[1fr_260px]">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        ) : units.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            No sellable units on this deal’s project. Link a unit to the deal or add inventory first.
          </div>
        ) : (
          <div className="grid gap-5 overflow-y-auto py-1 sm:grid-cols-[1fr_260px]">
            {/* ── Left: editable cost sheet ─────────────────────────────── */}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Select value={unitId ? String(unitId) : ""} onValueChange={(v) => setUnitId(Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Select a unit" /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.unit_number}{u.unit_type ? ` · ${u.unit_type}` : ""}
                        {u.carpet_area ? ` · ${u.carpet_area} sq.ft` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-xl border border-slate-200 p-3.5">
                <div className="mb-2.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Cost components
                </div>
                <div className="space-y-2.5">
                  {COMPONENTS.map(({ key, label }) => (
                    <CostComponentRow
                      key={key}
                      label={label}
                      value={components[key]}
                      onChange={(v) => setComponents((c) => ({ ...c, [key]: v }))}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2 rounded-xl border border-slate-200 p-3.5">
                <CostComponentRow
                  label="Negotiated price"
                  value={negotiated}
                  onChange={setNegotiated}
                />
                <div className="flex gap-1.5 pt-0.5">
                  <ModeChip active={isMatch} onClick={() => setMode(MODE_MATCH)} label="Match to negotiated" />
                  <ModeChip active={!isMatch} onClick={() => setMode(MODE_ASIS)} label="Keep components" />
                </div>
                {negotiatedExceedsSubtotal && (
                  <p className="flex items-start gap-1.5 text-[11.5px] text-amber-600">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    Negotiated price is above the sub-total — no discount applied. Increase a component instead.
                  </p>
                )}
              </div>
            </div>

            {/* ── Right: live summary ───────────────────────────────────── */}
            <div className="space-y-3 sm:sticky sm:top-0 sm:self-start">
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="mb-3 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Quote summary
                </div>
                <SummaryLine label="Sub-total" value={formatMoney(subtotal)} />
                {discount > 0 && (
                  <SummaryLine label="Discount" value={`− ${formatMoney(discount)}`} tone="danger" />
                )}
                <Separator className="my-2.5" />
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-slate-700">Total</span>
                  <span className="text-base font-semibold tabular-nums text-slate-900">{formatMoney(total)}</span>
                </div>
                {isMatch && negotiatedNum > 0 && !negotiatedExceedsSubtotal && (
                  <p className="mt-2 flex items-center gap-1.5 text-[11.5px] font-medium text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" /> Matches negotiated price
                  </p>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Payment plan
                </div>
                {hasClp ? (
                  <label className="flex cursor-pointer items-start gap-2 text-[13px] text-slate-600">
                    <Checkbox
                      className="mt-0.5 h-3.5 w-3.5"
                      checked={includeClp}
                      onCheckedChange={(v) => setIncludeClp(v === true)}
                    />
                    <span>
                      Include construction-linked plan
                      <span className="mt-0.5 block text-[11.5px] text-slate-400">
                        {milestoneCount} milestone{milestoneCount === 1 ? "" : "s"}
                      </span>
                    </span>
                  </label>
                ) : (
                  <p className="text-[12px] text-slate-400">No CLP configured for this unit.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button onClick={handleGenerate} disabled={isPending || loading || !unitId || subtotal <= 0} className="gap-1.5">
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {isPending ? "Generating…" : "Generate & preview"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ModeChip({ active, onClick, label }) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={
        "h-auto rounded-full px-2.5 py-1 text-[11.5px] font-medium transition-colors " +
        (active
          ? "bg-slate-800 text-white hover:bg-slate-700 hover:text-white"
          : "bg-slate-100 text-slate-500 hover:bg-slate-200")
      }
    >
      {label}
    </Button>
  )
}

function SummaryLine({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className={"text-[13px] tabular-nums " + (tone === "danger" ? "text-red-600" : "text-slate-700")}>
        {value}
      </span>
    </div>
  )
}
